import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { locationGetStatusOptions } from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { CityStatusBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import {
  ATTRIBUTE_PARAM,
  DRAWER_TAB_PARAM,
  EditableHotelFieldKey,
  SELECTED_ID_PARAM,
  formatUnknownValue,
} from './hotelMapShared';

const SEARCH_MODE_PARAM = 'searchMode';

type BuildCityUrlFunction = (cityId: string) => string;

type DetailRow = {
  id?: string;
  key: string;
  value: ReactNode;
  editableFieldKey?: EditableHotelFieldKey;
  editableFieldValue?: string;
};

function buildStatusRows(
  status: CityStatusBean | undefined,
  buildCityUrl: BuildCityUrlFunction
): Array<DetailRow> {
  if (!status) {
    return [
      {
        key: 'value',
        value: '-',
      },
    ];
  }

  const entries = Object.entries(status);

  const cityBeanIndex = entries.findIndex(([key]) => key === 'cityBean');
  const marketLargeCityIndex = entries.findIndex(([key]) => key === 'marketLargeCity');

  if (
    cityBeanIndex !== -1 &&
    marketLargeCityIndex !== -1 &&
    marketLargeCityIndex !== cityBeanIndex + 1
  ) {
    const [marketLargeCityEntry] = entries.splice(marketLargeCityIndex, 1);
    entries.splice(cityBeanIndex + 1, 0, marketLargeCityEntry);
  }

  return entries.map(([key, value]) => {
    // Special handling for marketLargeCity field in CityStatusBean
    if (
      key === 'marketLargeCity' &&
      value === false &&
      'market' in status &&
      status.market?.largeCity?.id
    ) {
      const largeCity = status.market.largeCity;
      const cityName = largeCity.name ?? 'Unknown';
      const suffixParts = [largeCity.stateCode, largeCity.countryCode].filter(
        (part): part is string => typeof part === 'string' && part.trim().length > 0
      );
      const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(', ')})` : '';
      const cityLabel = `${cityName}${suffix}`;

      return {
        key,
        value: (
          <Link
            to={buildCityUrl(largeCity.id!)}
            className="text-sky-700 underline hover:text-sky-800"
          >
            {cityLabel}
          </Link>
        ),
      };
    }

    return {
      key,
      value: formatUnknownValue(value),
    };
  });
}

type CityStatusSectionProps = {
  selectedCityId: string | undefined;
  searchParams: URLSearchParams;
};

export function CityStatusSection({
  selectedCityId,
  searchParams,
}: CityStatusSectionProps): ReactElement {
  const {
    data: selectedCityStatusResponse,
    error: selectedCityStatusError,
    isError: hasSelectedCityStatusError,
    isLoading: isSelectedCityStatusLoading,
  } = useQuery({
    ...(selectedCityId
      ? locationGetStatusOptions({
          path: {
            id: selectedCityId,
          },
        })
      : locationGetStatusOptions({
          path: {
            id: '',
          },
        })),
    enabled: Boolean(selectedCityId),
    staleTime: 60 * 1000,
  });
  const selectedCityStatusRows = useMemo(
    () =>
      buildStatusRows(selectedCityStatusResponse, (cityId) => {
        const newParams = new URLSearchParams(searchParams);
        const remainingAttributes = newParams
          .getAll(ATTRIBUTE_PARAM)
          .filter((attribute) => !attribute.startsWith('CITY:'));
        newParams.delete(ATTRIBUTE_PARAM);
        remainingAttributes.forEach((attribute) => newParams.append(ATTRIBUTE_PARAM, attribute));
        newParams.append(ATTRIBUTE_PARAM, `CITY:${cityId}`);
        newParams.set(SEARCH_MODE_PARAM, 'CITY');
        newParams.delete(SELECTED_ID_PARAM);
        newParams.set(DRAWER_TAB_PARAM, 'CITY');
        return `?${newParams.toString()}`;
      }),
    [selectedCityStatusResponse, searchParams]
  );

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
      {isSelectedCityStatusLoading ? (
        <p className="px-3 py-2 text-xs text-slate-600">Loading city status...</p>
      ) : null}
      {hasSelectedCityStatusError ? (
        <p className="px-3 py-2 text-xs text-rose-700">
          {getApiErrorMessage(selectedCityStatusError, 'Unable to load city status.')}
        </p>
      ) : null}
      {!isSelectedCityStatusLoading &&
      !hasSelectedCityStatusError &&
      selectedCityStatusResponse === undefined ? (
        <p className="px-3 py-2 text-xs text-slate-600">No city status found.</p>
      ) : null}
      {selectedCityStatusResponse !== undefined ? (
        <table className="w-full table-fixed border-collapse text-left text-sm">
          <tbody>
            {selectedCityStatusRows.map((row) => (
              <tr key={row.id ?? row.key} className="border-b border-slate-100 last:border-b-0">
                <th className="w-40 break-words bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 align-top">
                  {row.key}
                </th>
                <td className="break-words px-3 py-2 text-xs text-slate-800 align-top">
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
